import { addMinutes, differenceInMinutes } from "date-fns";
import { useMemo } from "react";
import { useUser } from "@/auth/useUser";
import { DEFAULT_CREATE_APPOINTMENT_DRAWER_WIDTH } from "@/components/booking/createAppointmentDrawer";
import AdditionalInfo from "@/components/common/additionalInfo/additionalInfo";
import { Provider } from "@/components/common/providerSection";
import { ServicesSection } from "@/components/common/services/servicesSection";
import { AppointmentServiceInput } from "@/components/common/services/types";
import { createHandleClearServices } from "@/components/common/services/utils";
import { reorder } from "@/components/common/verticalDraggableList/verticalDraggableList";
import { ServicesForBookingAppointmentQuery } from "@/graphql/queries/appointment/servicesForBookingAppointment.graphql.types";
import useErrorLogger from "@/utils/useErrorLogger";

export type AvailableService =
  ServicesForBookingAppointmentQuery["medspaServiceMenuItem"][number];

type Props = {
  disabled: boolean;
  selectedProviders: Provider[];
  availableProviders: Provider[];
  appointmentServices: AppointmentServiceInput[];
  availableServices: AvailableService[];
  startDate: Date;
  isRebooking: boolean;
  onAppointmentServicesChange: (services: AppointmentServiceInput[]) => void;
};

export const AppointmentServicesSection = ({
  selectedProviders,
  availableProviders,
  appointmentServices,
  availableServices,
  isRebooking,
  startDate,
  disabled,
  onAppointmentServicesChange,
}: Props) => {
  const { userMedspa } = useUser();
  const logError = useErrorLogger();

  const areSelectedServicesSameApptType = useMemo(
    () =>
      appointmentServices.length === 0 ||
      appointmentServices
        .map((service) => service.serviceMenuItem.appointmentType)
        .reduce((acc, type) => acc.add(type), new Set()).size === 1,
    [appointmentServices]
  );

  const onUpdateAppointmentServiceProvider = (
    index: number,
    provider: Provider
  ) => {
    const newAppointmentServices = [...appointmentServices];
    newAppointmentServices[index] = {
      ...newAppointmentServices[index],
      provider,
    };
    onAppointmentServicesChange(newAppointmentServices);
  };

  /**
   * Remove appointment service and shift consecutive appointment services time by
   * the duration of the removed appointment service to fill the gap
   * @TODO: Good candidate for unit tests
   */
  const handleRemoveAppointmentService = (index: number) => {
    const removedAppointmentServiceDuration = differenceInMinutes(
      appointmentServices[index].endTime,
      appointmentServices[index].startTime
    );
    const newAppointmentServices = appointmentServices.filter(
      (_, i) => i !== index
    );
    for (let i = index; i < newAppointmentServices.length; i++) {
      newAppointmentServices[i].startTime = addMinutes(
        newAppointmentServices[i].startTime,
        -removedAppointmentServiceDuration
      );
      newAppointmentServices[i].endTime = addMinutes(
        newAppointmentServices[i].endTime,
        -removedAppointmentServiceDuration
      );
    }
    onAppointmentServicesChange(newAppointmentServices);
  };

  /**
   * Add appointment service using selected service menu item
   * For provider, choose first service eligible provider from selected providers or available providers
   * For start and end time, use the end time of the last appointment service or the start time of the appointment
   * @TODO: Good candidate for unit tests
   */
  const handleAddService = (serviceMenuItem: AvailableService) => {
    const newAppointmentServices = [...appointmentServices];

    const searchableProviders =
      selectedProviders.length > 0 ? selectedProviders : availableProviders;

    const currentProvider =
      userMedspa &&
      searchableProviders.find(
        (providerUserMedspa) =>
          providerUserMedspa.id === userMedspa.id &&
          serviceMenuItem.eligibleProviders.some(
            (eligibleProvider) =>
              eligibleProvider.userMedspa.id === userMedspa.id
          )
      );

    const provider =
      currentProvider ||
      searchableProviders.find((providerUserMedspa) =>
        serviceMenuItem.eligibleProviders.some(
          (eligibleProvider) =>
            eligibleProvider.userMedspa.id === providerUserMedspa.id
        )
      );

    if (provider) {
      const startTime =
        newAppointmentServices.length > 0
          ? newAppointmentServices.at(-1).endTime
          : startDate;
      const endTime = addMinutes(startTime, serviceMenuItem.durationInMinutes);
      newAppointmentServices.push({
        serviceMenuItem,
        provider,
        startTime,
        endTime,
      });
      onAppointmentServicesChange(newAppointmentServices);

      return;
    }
    logError(
      new Error(
        `[ServicesSection] Cannot find eligible provider for service ${serviceMenuItem.name}`
      )
    );
  };

  /**
   * Reorder appointment services and shift appointment services times
   * Preserve gaps between appointment services
   * @TODO: Good candidate for unit tests
   */
  const handleReorderAppointmentServices = (
    sourceIndex: number,
    destinationIndex: number
  ) => {
    // array of gaps between appointment services
    // gap[i] = appointmentServices[i+1].startTime - appointmentServices[i].endTime
    const gaps = appointmentServices
      .slice(0, -1)
      .map((_, i) =>
        differenceInMinutes(
          appointmentServices[i + 1].startTime,
          appointmentServices[i].endTime
        )
      );

    const newAppointmentServices = reorder(
      [...appointmentServices],
      sourceIndex,
      destinationIndex
    );
    const updateFromIndex = Math.min(sourceIndex, destinationIndex);

    for (let i = updateFromIndex; i < newAppointmentServices.length; i++) {
      const newStartTime =
        i > 0
          ? addMinutes(newAppointmentServices[i - 1].endTime, gaps[i - 1])
          : startDate;
      const duration = differenceInMinutes(
        newAppointmentServices[i].endTime,
        newAppointmentServices[i].startTime
      );
      newAppointmentServices[i].startTime = newStartTime;
      newAppointmentServices[i].endTime = addMinutes(newStartTime, duration);
    }
    onAppointmentServicesChange(newAppointmentServices);
  };

  const handleClearServices = createHandleClearServices(
    onAppointmentServicesChange
  );

  const rebookingInfo = isRebooking ? (
    <AdditionalInfo
      variant="info"
      text="Services default to the same as today's appointment."
      sx={{ maxWidth: DEFAULT_CREATE_APPOINTMENT_DRAWER_WIDTH, m: 0, mb: 1 }}
    />
  ) : null;

  const appointmentTypeWarning = !areSelectedServicesSameApptType ? (
    <AdditionalInfo
      variant="error"
      text="The services above include both in person and a telehealth format types. An appointment can only have one format type."
      sx={{ maxWidth: DEFAULT_CREATE_APPOINTMENT_DRAWER_WIDTH, mt: 0 }}
    />
  ) : null;

  return (
    <ServicesSection
      services={appointmentServices}
      availableServices={availableServices}
      selectedProviders={selectedProviders}
      availableProviders={availableProviders}
      disabled={disabled}
      onAddService={handleAddService}
      onRemoveService={handleRemoveAppointmentService}
      onReorderServices={handleReorderAppointmentServices}
      onClearServices={handleClearServices}
      onUpdateServiceProvider={onUpdateAppointmentServiceProvider}
      additionalInfo={rebookingInfo}
      warningInfo={appointmentTypeWarning}
    />
  );
};
